import React, { useEffect, useRef, useState } from "react";
import { FaTrophy } from "react-icons/fa";
import { HiDownload } from "react-icons/hi";
import { contestResult } from "../../ApiServices/ApiServices";
import CodeQueueSpinner, { handleError } from "../../Utils";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import logo from "../../assets/logo.png";
import medal from "../../assets/ImageForCertificates/medal_logo.png";
import Volunteers from "./Volunteers";
import VerifyContestOtp from "./VerifyContestOtp";

const Contest = () => {
  const [contestData, setContestData] = useState(null);
  const [showCertificate, setShowCertificate] = useState(true);
  const certificateRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showVerificationModel, setShowVerificationModel] = useState(false);

  const downloadCertificate = async () => {
    const input = certificateRef.current;

    const canvas = await html2canvas(input, {
      scale: 2,
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "px",
      format: [794, 900], // A4 size in pixels at 96 DPI
    });

    pdf.addImage(imgData, "PNG", 0, 0, 794, 900);
    pdf.save("CodeQueue_Certificate.pdf");
  };

  const fetchContestResult = async () => {
    try {
      setLoading(true);
      const result = await contestResult();
      setContestData(result?.data);
    } catch (error) {
      return handleError(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Codequeue - Contest";
    fetchContestResult();
  }, []);

  return (
    <div className="flex flex-wrap mb-8">
      <div className="max-w-xl mx-auto mt-12 p-8 bg-gradient-to-br from-white to-gray-100 shadow-xl rounded-2xl border border-gray-200">
        {loading ? (
          <CodeQueueSpinner />
        ) : (
          <div>
            <div className="text-center mb-6">
              <h2 className="text-3xl font-extrabold text-gray-800 flex items-center justify-center gap-2">
                <FaTrophy className="text-yellow-500" />
                {contestData?.contestName || "NA"}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Showcasing your coding excellence!
              </p>
            </div>

            <div className="space-y-4 text-base text-gray-700 font-medium bg-white rounded-xl p-6 shadow-sm border">
              <div>
                <span className="font-semibold text-gray-600">Name :</span>{" "}
                {contestData?.name}
              </div>
              <div>
                <span className="font-semibold text-gray-600">Score:</span>{" "}
                {contestData?.score}
              </div>
              <div>
                <span className="font-semibold text-gray-600">Rank:</span>{" "}
                {contestData?.rank || "NA"}
              </div>
              <div>
                <span className="font-semibold text-gray-600">
                  Certificate:
                </span>{" "}
                {contestData?.certificateType || "NA"}
                {contestData?.certificateType && (
                  <button
                    className="inline-flex items-center ml-3 text-blue-600 hover:underline text-sm font-normal"
                    onClick={downloadCertificate}
                  >
                    <HiDownload className="mr-1" />
                    Download
                  </button>
                )}
              </div>
              <div>
                <span className="font-semibold text-gray-600">
                  Participated Date:
                </span>{" "}
                {contestData?.completedOn || "NA"}
              </div>
            </div>

            <div className="mt-8 text-center">
              <button
                // disabled
                onClick={setShowVerificationModel}
                className="bg-green-400 text-white px-5 py-2.5 rounded-full shadow-md transition duration-200 font-semibold"
              >
                Go to Contest
              </button>
            </div>
          </div>
        )}

        {/* certificate part for all the participants*/}
        {showCertificate && (
          <div
            className="flex-col"
            style={{
              position: "absolute",
              left: "-9999px", // Move it far offscreen
              top: "0",
              height: 0,
              overflow: "hidden",
            }}
          >
            {true && (
              <div>
                <div
                  ref={certificateRef}
                  className="bg-blue-900 flex flex-col items-center justify-center p-4"
                  style={{
                    width: "794px",
                    height: "900px",
                    padding: "2rem",
                    boxSizing: "border-box",
                    position: "relative",
                  }}
                >
                  <div className="bg-white border-8 border-cyan-400 p-8 w-full h-full max-w-4xl text-center relative">
                    {/* Decorative Corners */}
                    <div className="absolute border-t-4 border-l-4 border-cyan-400 top-4 left-4 w-20 h-20"></div>
                    <div className="absolute border-t-4 border-r-4 border-cyan-400 top-4 right-4 w-20 h-20"></div>
                    <div className="absolute border-b-4 border-l-4 border-cyan-400 bottom-4 left-4 w-20 h-20"></div>
                    <div className="absolute border-b-4 border-r-4 border-cyan-400 bottom-4 right-4 w-20 h-20"></div>

                    <div className="flex justify-center items-center">
                      <div className="border-t-4 border-blue-900 w-1/4 mx-1"></div>
                      <div className="text-blue-900 text-2xl font-bold border-2 border-blue-900 p-2">
                        {/* CSS Box Icon */}
                        <div className="relative w-5 h-5 border-2 border-blue-900">
                          <div className="absolute inset-1 bg-blue-900"></div>
                        </div>
                      </div>
                      <div className="border-t-4 border-blue-900 w-1/4 mx-1"></div>
                    </div>

                    {/* Company Name */}
                    <div className="mb-6 mt-4">
                      <h1
                        className="text-5xl font-bold tracking-wide"
                        style={{
                          fontFamily:
                            "'Segoe UI', 'Helvetica Neue', sans-serif",
                        }}
                      >
                        <span className="text-black">Code</span>
                        <span className="text-blue-600">Queue</span>
                      </h1>
                    </div>

                    {/* Header */}
                    <div className="mb-4">
                      <div className="text-3xl font-bold text-blue-800 tracking-wide">
                        CERTIFICATE
                      </div>
                      <div className="text-sm text-gray-700 tracking-wider mt-1">
                        OF {contestData?.certificateType?.toUpperCase()}
                      </div>
                    </div>

                    {/* Presented to */}
                    <div className="text-sm text-gray-700 mt-6">
                      THIS CERTIFICATE IS PROUDLY PRESENTED TO
                    </div>
                    <div className="text-4xl text-blue-800 font-cursive italic font-semibold mt-2">
                      {contestData?.name}
                    </div>

                    {/* Medal Image */}
                    <div className="flex justify-center mt-4">
                      <img
                        src={medal}
                        alt="Medal Icon"
                        className="h-20 w-20 object-contain"
                      />
                    </div>

                    {/* Certificate Body */}
                    <div className="text-gray-700 text-md mt-6 max-w-2xl mx-auto leading-relaxed">
                      <p>
                        This is to certify that {contestData?.name} has
                        successfully participated in{" "}
                        <strong>CodeQueue CodeSprint 2.0</strong> held on 10th
                        June 2025 at <span className="font-bold">TIT Bhopal Campus</span>
                      </p>
                      <p className="mt-2">
                        We appreciate their contribution, enthusiasm, and
                        technical skills
                      </p>
                    </div>

                    {/* Signatures */}
                    <div className="flex-col justify-between items-center mt-6 px-10">
                      {/* Company Logo */}
                      <div className="w-24 h-24 flex items-center justify-center mb-6">
                        <img
                          src={logo}
                          alt="CodeQueue Logo"
                          className="object-contain w-24 h-24"
                        />
                      </div>
                      <p className="text-md text-gray-700 font-medium text-center">
                        Awarded on behalf of <strong>CodeQueue</strong>, with
                        appreciation for your outstanding participation.
                      </p>
                    </div>

                    {/* Footer Decoration */}
                    <div className="mt-8 flex justify-center items-center">
                      <div className="border-t-4 border-blue-900 w-1/4 mx-1"></div>
                      <div className="text-blue-900 text-2xl font-bold border-2 border-blue-900 p-2">
                        {/* CSS Box Icon */}
                        <div className="relative w-5 h-5 border-2 border-blue-900">
                          <div className="absolute inset-1 bg-blue-900"></div>
                        </div>
                      </div>
                      <div className="border-t-4 border-blue-900 w-1/4 mx-1"></div>
                    </div>
                  </div>
                </div>
                {/* Download Button */}
                {/* <button
                  onClick={downloadCertificate}
                  className="text-center mt-6 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded shadow-lg"
                >
                  Download Certificate
                </button> */}
              </div>
            )}
          </div>
        )}
      </div>

      {contestData?.passKey && (
        <div className="max-w-xl mx-auto mt-12 p-8 bg-gradient-to-br from-white to-gray-100 shadow-xl rounded-2xl border border-gray-200">
          <Volunteers passKey={contestData?.passKey} />
        </div>
      )}

      {showVerificationModel && <VerifyContestOtp setShowModal={setShowVerificationModel} email={contestData?.email}/>}
    </div>
  );
};

export default Contest;
